import { ColumnDef } from "@tanstack/react-table";
import {
  CrossInRedCircle,
  EditIcon,
  ReplaceIcon,
  TrashAltIcon
} from "assets/icons";
import { useLanguage } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { t } from "i18next";
import moment from "moment";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import {
  ItemsTable,
  TableItemActionsControl,
  TableItemStatusControl,
  PersonInfo,
  Status,
  Icon
} from "ui";

import { useUpdateManagerStatus } from "../../apis";
import {
  INVENTORY_MANAGER_STATUSES_LIST,
  InventoryManagerStatuses
} from "../../constants";
import { InventoryManagerListItem } from "../../inventory.types";
import { InventoryManagersTableProps } from "./InventoryManagersTable.types";

const InventoryManagersTable = ({
  managers,
  sorting,
  refetchInventoryManagers,
  setSorting,
  onChangeDeleteStatus,
  onEditManager,
  isLoading
}: InventoryManagersTableProps) => {
  const { currentLanguage } = useLanguage();

  const { mutate: updateManagerStatus } = useUpdateManagerStatus({
    onSuccess: () => {
      toast.success(t("Inventory manager status updated successfully"));
      setOpenedItemStatusesIndex(null);
      refetchInventoryManagers();
    }
  });

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);
  const [openedItemStatusesIndex, setOpenedItemStatusesIndex] = useState<
    number | null
  >(null);

  const handleManagerStatusUpdate = (isActive: boolean) => {
    if (openedItemStatusesIndex !== null) {
      updateManagerStatus({
        managerId: managers[openedItemStatusesIndex].id,
        isActive
      });
    }
  };

  const statusesList: Status[] = INVENTORY_MANAGER_STATUSES_LIST.map(
    (orStatus) => ({
      ...orStatus,
      onClick: () =>
        handleManagerStatusUpdate(
          orStatus.value === InventoryManagerStatuses.Active
        )
    })
  );

  const columns: Array<
    ColumnDef<InventoryManagerListItem, InventoryManagerListItem[]>
  > = useMemo(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: t("Inventory Manager"),
        size: 48,
        minSize: 48,
        cell: ({ row }) => (
          <PersonInfo
            image={managers[row.index].image}
            imageMarker={
              managers[row.index].isDeleted ? (
                <Icon as={CrossInRedCircle} width={18} height={18} />
              ) : null
            }
            personName={`${managers[row.index].firstname} ${
              managers[row.index].lastname
            }`}
          />
        )
      },
      {
        header: t("Phone"),
        enableSorting: false,
        size: 30,
        minSize: 30,
        cell: ({ row }) => managers[row.index].phoneNumber
      },
      {
        id: "isActive",
        header: t("Status"),
        accessorKey: "isActive",
        size: 18,
        minSize: 18,
        cell: ({ row }) => (
          <TableItemStatusControl
            rowIndex={row.index}
            activeStatusIndex={managers[row.index].isActive ? 0 : 1}
            openedItemStatusesIndex={openedItemStatusesIndex}
            setOpenedItemStatusesIndex={setOpenedItemStatusesIndex}
            statusesList={statusesList}
            disabled={row.original.isDeleted}
          />
        )
      },

      {
        accessorKey: "dotsColumn",
        header: () => null,
        size: 4,
        minSize: 4,
        cell: ({ row }) => {
          const daysLeftToRestore = Math.ceil(
            moment(row.original.accountRestorationDeadline).diff(
              moment(),
              "days",
              true
            )
          );

          if (row.original.isDeleted && daysLeftToRestore <= 0) {
            return null;
          }

          return (
            <TableItemActionsControl
              actionsList={[
                ...(row.original.isDeleted
                  ? []
                  : [
                      {
                        label: "Edit",
                        Icon: (
                          <EditIcon fill={"#090914"} width={18} height={18} />
                        ),
                        onClick: () => onEditManager(managers[row.index])
                      }
                    ]),
                {
                  label: row.original.isDeleted ? t("Restore") : t("Delete"),
                  Icon: row.original.isDeleted ? (
                    <ReplaceIcon fill={"#090914"} width={17} height={17} />
                  ) : (
                    <TrashAltIcon fill={"#ff0033"} width={18} height={18} />
                  ),
                  labelColor: row.original.isDeleted ? "#090914" : "#ff0033",
                  onClick: () => onChangeDeleteStatus(row.original)
                }
              ]}
              rowIndex={row.index}
              setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
              openedItemOptionsIndex={openedItemOptionsIndex}
            />
          );
        },
        enableSorting: false
      }
    ],
    [managers, openedItemOptionsIndex, openedItemStatusesIndex, currentLanguage]
  );

  return (
    <ItemsTable
      columns={columns}
      data={managers}
      sorting={sorting}
      skeletonHeight={65}
      setSorting={setSorting}
      isLoading={isLoading}
    />
  );
};

export default InventoryManagersTable;
